// src/components/financials/ChurchEntitySettings.js
// Updated: 01/25/2026 - Added Logo Upload Feature for Tax Statements

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, Grid, Alert, CircularProgress, Avatar 
} from '@mui/material';
import { Save, Gavel, PhotoCamera } from '@mui/icons-material';
import { db, storage } from '../../services/firebase'; // Ensure storage is exported from firebase.js
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';

const ChurchEntitySettings = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;

  const [formData, setFormData] = useState({
    legalName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    taxId: '',
    logoUrl: '' // <--- Added field
  });
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (churchId) fetchSettings();
  }, [churchId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'churches', churchId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        const lp = data.legalProfile || {};
        setFormData({
            legalName: lp.legalName || data.name || '',
            address: lp.address || data.address || '',
            city: lp.city || data.city || '',
            state: lp.state || data.state || '',
            zip: lp.zip || data.zip || '',
            taxId: lp.taxId || '',
            logoUrl: lp.logoUrl || '' // <--- Set existing logo
        });
      }
    } catch (e) {
      console.error("Error fetching entity settings:", e);
    }
    setLoading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !churchId) return;

    setUploading(true);
    try {
      // Create reference: churches/ID/logos/filename
      const storageRef = ref(storage, `churches/${churchId}/logos/legal_logo_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      setFormData(prev => ({ ...prev, logoUrl: url }));
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Logo upload failed: " + error.message);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setSuccess(false);
    try {
      const docRef = doc(db, 'churches', churchId);
      await updateDoc(docRef, {
        legalProfile: {
            ...formData, // Saves all fields including logoUrl
            updatedAt: new Date().toISOString()
        }
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      alert("Error saving settings: " + e.message);
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Gavel color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">IRS Legal Entity Profile</Typography>
      </Box>

      {/* --- LOGO UPLOAD SECTION --- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, gap: 1 }}>
        <Avatar 
          src={formData.logoUrl} 
          sx={{ width: 100, height: 100, border: '1px solid #eee' }}
          variant="rounded"
        >
          <PhotoCamera />
        </Avatar>
        <Button 
          variant="outlined" 
          component="label" 
          size="small" 
          startIcon={uploading ? <CircularProgress size={16} /> : <PhotoCamera />}
          disabled={uploading}
        >
          {formData.logoUrl ? "Change Logo" : "Upload Logo"}
          <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
        </Button>
      </Box>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Enter the <strong>full legal name</strong> and registered address for tax compliance. 
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
            <TextField 
                fullWidth label="Full Legal Name" 
                value={formData.legalName} onChange={(e) => handleChange('legalName', e.target.value)} 
            />
        </Grid>
        <Grid item xs={12}>
            <TextField 
                fullWidth label="Federal Tax ID (EIN)" 
                value={formData.taxId} onChange={(e) => handleChange('taxId', e.target.value)} 
            />
        </Grid>
        <Grid item xs={12}>
            <TextField 
                fullWidth label="Registered Street Address" 
                value={formData.address} onChange={(e) => handleChange('address', e.target.value)} 
            />
        </Grid>
        <Grid item xs={6}>
            <TextField 
                fullWidth label="City" 
                value={formData.city} onChange={(e) => handleChange('city', e.target.value)} 
            />
        </Grid>
        <Grid item xs={3}>
            <TextField 
                fullWidth label="State" 
                value={formData.state} onChange={(e) => handleChange('state', e.target.value)} 
            />
        </Grid>
        <Grid item xs={3}>
            <TextField 
                fullWidth label="Zip" 
                value={formData.zip} onChange={(e) => handleChange('zip', e.target.value)} 
            />
        </Grid>

        <Grid item xs={12} sx={{ mt: 1 }}>
            <Button 
                fullWidth variant="contained" 
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                onClick={handleSave}
                disabled={loading || uploading}
            >
                {loading ? "Saving..." : "Save Legal Profile"}
            </Button>
        </Grid>
      </Grid>

      {success && <Alert severity="success" sx={{ mt: 2 }}>Legal profile updated!</Alert>}
    </Paper>
  );
};

export default ChurchEntitySettings;